import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { buildPrivacyConsentText } from "../../../../lib/forms/privacyConsent";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";
import type { FormFieldInputProps, FormFieldRef } from "../../types";

interface PrivacyInputProps extends FormFieldInputProps {
  validation?: (value: string) => boolean;
}

const PrivacyInput = forwardRef<FormFieldRef, PrivacyInputProps>(
  (props, ref) => {
    const shellRef = useRef<HTMLLabelElement>(null);
    const inputId = useId();
    const [checked, setChecked] = useState(props.initialValue === "1");
    const [validationState, setValidationState] = useState(true);
    const consentText = buildPrivacyConsentText();

    const value = checked ? "1" : "";

    const commitValue = (next: boolean) => {
      setChecked(next);
      reportFieldValue(props.fieldName, next ? "1" : "", props.onValueChange);
    };

    const clear = () => commitValue(false);

    const validate = () => {
      if (!props.validation) return true;
      const result = props.validation(value);
      setValidationState(result);
      return result;
    };

    useImperativeHandle(ref, () => ({
      setValidationState,
      value,
      fieldId: props.fieldId,
      fieldName: props.fieldName,
      scrollToElement: (options) => {
        shellRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          ...options,
        });
      },
      validate,
      clear,
    }));

    return (
      <div className="mt-4 w-full sm:col-span-2">
        <label
          ref={shellRef}
          htmlFor={inputId}
          className={[
            "grid scroll-mt-24 grid-cols-[1.125rem_minmax(0,1fr)] items-start gap-x-3 rounded-xl border px-3.5 py-3.5 sm:scroll-mt-8 sm:px-4 sm:py-4",
            "cursor-pointer transition-colors",
            !validationState
              ? "border-red-300 bg-red-50/40"
              : "border-slate-200 bg-slate-50/60 hover:border-slate-300",
          ].join(" ")}
        >
          <span className="relative flex h-[1.125rem] w-[1.125rem] shrink-0 items-center justify-center pt-0.5">
            <input
              id={inputId}
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                commitValue(e.target.checked);
                if (e.target.checked) setValidationState(true);
              }}
              onBlur={validate}
              name={props.fieldName}
              aria-invalid={!validationState}
              className="form-accent-checkbox peer sr-only"
            />
            <span
              className={[
                "flex h-[1.125rem] w-[1.125rem] items-center justify-center rounded border-2 transition-colors",
                "peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500/30 peer-focus-visible:ring-offset-2",
                checked
                  ? "border-[var(--form-accent,#EA3005)] bg-[var(--form-accent,#EA3005)]"
                  : "border-slate-300 bg-white",
              ].join(" ")}
              aria-hidden
            >
              {checked ? (
                <svg
                  className="h-2.5 w-2.5 text-white"
                  viewBox="0 0 12 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 1L4.125 7.875L1 4.75"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : null}
            </span>
          </span>

          <span className="font-onest text-[13px] leading-[1.65] text-slate-600 sm:text-sm">
            {consentText}
          </span>
        </label>

        {!validationState ? (
          <p
            className="mt-2 pl-[calc(0.875rem+1.125rem+0.75rem)] font-onest text-xs leading-snug text-red-600 sm:pl-[calc(1rem+1.125rem+0.75rem)]"
            role="alert"
          >
            Необходимо дать согласие на обработку персональных данных
          </p>
        ) : null}
      </div>
    );
  },
);

PrivacyInput.displayName = "PrivacyInput";

export { PrivacyInput };
export type { PrivacyInputProps };
